import { useAsyncData } from '@repo/ui/dataProvider'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { useT } from '@repo/ui/hooks'
import { subDays } from 'date-fns'
import { ChevronRight } from 'lucide-react'

import { BeneficiaryLink } from '@/components/beneficiary/BeneficiaryLink'
import { Link } from '@/components/Link'
import { SmartBlock } from '@/components/SmartBlock'
import { useView } from '@/hooks'
import { Beneficiary } from '@/store/beneficiary'
import { getCurrentOrganization } from '@/store/helpers'
import { useStoreState } from '@/store/hooks'

const listLimit = 3
const unseenLimit = 15 // days

export function DashboardSmartBlocks({ beneficiaries }: { beneficiaries: Beneficiary[] }) {
  const { t } = useT({ keyPrefix: 'dashboard' })
  const isThinking = useStoreState((state) => state.assistant.isThinking)
  const lastXdays = subDays(new Date(), unseenLimit).getTime()

  const unseenBeneficiaries = beneficiaries
    ?.filter((b) => b.updatedAt <= lastXdays)
    .map((b) => beneficiaries.find((ben) => ben.id === b.id))
    .filter((b) => b !== undefined)
  const unseenShortList = unseenBeneficiaries?.slice(0, listLimit)
  const unseenMoreList = unseenBeneficiaries?.slice(listLimit)

  const recentBeneficiaries = beneficiaries
    ?.filter((b) => b.updatedAt > lastXdays)
    .map((b) => beneficiaries.find((ben) => ben.id === b.id))
    .filter((b) => b !== undefined)

  const recentShortList = recentBeneficiaries?.slice(0, listLimit)
  const recentMoreList = recentBeneficiaries?.slice(listLimit)

  return (
    <>
      {recentShortList.length > 0 && (
        <SmartBlock isThinking={isThinking} title={t('recent', { colon: true })} variant="default">
          <>
            {recentShortList.map((b) => (
              <BeneficiaryLink
                key={b.id}
                className="text-base"
                id={b.id}
                fullName={b.fullName}
                withIcon={false}
              />
            ))}
            <MoreList beneficiaries={recentMoreList} />
          </>
        </SmartBlock>
      )}
      {unseenShortList.length > 0 && (
        <SmartBlock isThinking={isThinking} title={t('unseen', { colon: true })}>
          <>
            {unseenShortList.map((b) => (
              <BeneficiaryLink
                key={b.id}
                id={b.id}
                fullName={b.fullName}
                className="text-base"
                withIcon={false}
              />
            ))}
            <MoreList beneficiaries={unseenMoreList} />
          </>
        </SmartBlock>
      )}
    </>
  )
}

function MoreList({ beneficiaries }: { beneficiaries: Beneficiary[] | undefined }) {
  const { t } = useT({ keyPrefix: 'dashboard' })
  const view = useView()
  const {
    data: [organization],
  } = useAsyncData([getCurrentOrganization])
  if (!organization || !beneficiaries || beneficiaries.length === 0) return null
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Link className="mt-2 gap-px text-sm no-underline">
          <ChevronRight className="size-4" />
          {t('moreBeneficiaries', { count: beneficiaries.length })}
        </Link>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="rounded-md" side="bottom" align="start" sideOffset={4}>
        <DropdownMenuGroup>
          {beneficiaries.map((b, i) => (
            <div key={b.id}>
              {i > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={view.beneficiary(b.id).profile.navigate}>
                {b.fullName}
              </DropdownMenuItem>
            </div>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
